import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, Eyebrow } from "./Primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = ["fan", "creator", "agency"];

export const WaitlistForm = () => {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("fan");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError(t.waitlist.invalidEmail);
    if (!consent) return setError(t.waitlist.consentRequired);
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, { email, role, consent, lang });
      setDone(true);
      toast.success(t.waitlist.success);
    } catch (err) {
      const status = err.response?.status;
      const msg = status === 409 ? t.waitlist.duplicate : t.waitlist.error;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="waitlist" data-testid="waitlist-section" className="mx-auto max-w-7xl px-4 pb-28 pt-8 sm:px-6 lg:px-8">
      <div className="hero-card grain relative overflow-hidden rounded-[2rem] border border-white/70 p-6 sm:p-10 lg:p-14">
        <div className="relative grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow>{t.waitlist.eyebrow}</Eyebrow></Reveal>
            <Reveal delay={0.08}>
              <h2 data-testid="waitlist-title" className="font-display mt-4 text-3xl font-bold leading-[1.05] tracking-tight text-ink sm:text-4xl lg:text-5xl">{t.waitlist.title}</h2>
            </Reveal>
            <Reveal delay={0.16}><p className="mt-5 text-base leading-relaxed text-ink-2 sm:text-lg">{t.waitlist.sub}</p></Reveal>
            <Reveal delay={0.22}><p className="mt-6 text-xs text-mist">{t.waitlist.note}</p></Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-7">
            {done ? (
              <div data-testid="waitlist-success" className="card-soft flex h-full flex-col items-start justify-center gap-4 p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-violet-100 text-violet-700"><CheckCircle2 size={24} /></span>
                <p className="font-display text-2xl font-bold text-ink">{t.waitlist.success}</p>
                <p className="text-sm text-ink-2">{email}</p>
              </div>
            ) : (
              <form data-testid="waitlist-form" onSubmit={submit} noValidate className="card-soft space-y-6 p-6 sm:p-8">
                <div>
                  <label htmlFor="wl-email" className="text-sm font-semibold text-ink">{t.waitlist.email}</label>
                  <input
                    id="wl-email"
                    data-testid="waitlist-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.waitlist.emailPh}
                    className="mt-2 w-full rounded-xl border border-line bg-canvas px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-mist focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-100"
                  />
                </div>
                <div>
                  <p className="text-sm font-semibold text-ink">{t.waitlist.iam}</p>
                  <div role="radiogroup" className="mt-2 grid grid-cols-3 gap-2">
                    {ROLES.map((r) => (
                      <button
                        key={r}
                        type="button"
                        role="radio"
                        aria-checked={role === r}
                        data-testid={`waitlist-role-${r}-radio`}
                        onClick={() => setRole(r)}
                        className={`pill border px-3 py-2.5 text-sm font-medium ${role === r ? "border-violet-600 bg-violet-600 text-white" : "border-line bg-white text-ink-2 hover:border-violet-300"}`}
                      >
                        {t.waitlist.roles[r]}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-ink-2">
                  <input
                    type="checkbox"
                    data-testid="waitlist-consent-checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-violet-600"
                  />
                  <span>
                    {t.waitlist.consent}{" "}
                    <Link to="/legal/privacy" className="font-semibold text-violet-700 underline-offset-2 hover:underline">{t.waitlist.consentLink}</Link>.
                  </span>
                </label>
                {error && <p data-testid="waitlist-error" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
                <button type="submit" disabled={loading} data-testid="waitlist-submit-button" className="pill btn-primary w-full px-6 py-3.5 text-sm font-semibold disabled:opacity-60">
                  {loading ? t.waitlist.submitting : t.waitlist.submit}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
